import React, { useState, useEffect, useRef, useCallback } from 'react';

const MapBoxGPSComponent = ({ latitude = 36.1437044, longitude = -121.564593, locationName = "Big Sur", campingLocations = [] }) => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const markers = useRef([]); // Track markers for cleanup
    const [mapLoaded, setMapLoaded] = useState(false);
    const [mapError, setMapError] = useState(false);
    const [currentStyle, setCurrentStyle] = useState('streets');
    const [loadingMap, setLoadingMap] = useState(true); // Start loading immediately

    const MAPBOX_TOKEN = 'pk.eyJ1IjoiZ2VyYXJkaG9yYW4iLCJhIjoiY21idHF5bGZvMDZvczJucG01djZqdGc3OSJ9.OrzdnpqG3pF3afJtrNAkVw';

    const getMapStyle = (style) => {
        switch (style) {
            case 'satellite':
                return 'mapbox://styles/mapbox/satellite-streets-v12';
            case 'terrain':
                return 'mapbox://styles/mapbox/outdoors-v12';
            case 'dark':
                return 'mapbox://styles/mapbox/dark-v11';
            default:
                return 'mapbox://styles/mapbox/streets-v12';
        }
    };

    // Create a circle polygon for the radius
    const createCircle = useCallback((center, radiusKm) => {
        const points = 64;
        const coords = [];

        for (let i = 0; i < points; i++) {
            const angle = (i / points) * 2 * Math.PI;
            const dx = radiusKm * Math.cos(angle);
            const dy = radiusKm * Math.sin(angle);

            // Convert km to degrees (approximate)
            const deltaLat = dy / 111;
            const deltaLng = dx / (111 * Math.cos(center[1] * Math.PI / 180));

            coords.push([center[0] + deltaLng, center[1] + deltaLat]);
        }

        coords.push(coords[0]); // Close the circle

        return {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [coords]
            }
        };
    }, []);

    // Cleanup function
    const cleanup = useCallback(() => {
        // Remove all markers
        markers.current.forEach(marker => {
            try {
                marker.remove();
            } catch (error) {
                console.log('Error removing marker during cleanup:', error);
            }
        });
        markers.current = [];

        // Remove map
        if (map.current) {
            map.current.remove();
            map.current = null;
        }
    }, []);

    const addMapFeatures = useCallback(() => {
        if (!map.current || !mapLoaded) return;

        try {
            console.log('Adding map features...');

            // Remove existing sources and layers with better error handling
            ['radius-fill', 'radius-circle'].forEach(id => {
                try {
                    if (map.current.getLayer(id)) {
                        map.current.removeLayer(id);
                    }
                } catch (error) {
                    console.log(`Layer ${id} not found or already removed`);
                }
            });

            ['radius-circle'].forEach(id => {
                try {
                    if (map.current.getSource(id)) {
                        map.current.removeSource(id);
                    }
                } catch (error) {
                    console.log(`Source ${id} not found or already removed`);
                }
            });

            // Add 50-mile radius circle
            const radiusKm = 80.47; // 50 miles in kilometers
            const radiusCircle = createCircle([longitude, latitude], radiusKm);

            map.current.addSource('radius-circle', {
                type: 'geojson',
                data: radiusCircle
            });

            // Add circle outline
            map.current.addLayer({
                id: 'radius-circle',
                type: 'line',
                source: 'radius-circle',
                paint: {
                    'line-color': '#ff6b6b',
                    'line-width': 3,
                    'line-opacity': 0.8
                }
            });

            // Add semi-transparent fill
            map.current.addLayer({
                id: 'radius-fill',
                type: 'fill',
                source: 'radius-circle',
                paint: {
                    'fill-color': '#ff6b6b',
                    'fill-opacity': 0.1
                }
            });

            // Remove existing markers properly
            markers.current.forEach(marker => {
                try {
                    marker.remove();
                } catch (error) {
                    console.log('Error removing marker:', error);
                }
            });
            markers.current = []; // Clear the markers array

            // Add center marker
            const centerMarker = new window.mapboxgl.Marker({
                color: '#ff0000',
                scale: 1.2
            })
                .setLngLat([longitude, latitude])
                .setPopup(new window.mapboxgl.Popup().setHTML(`
          <div class="text-center p-2">
            <strong>${locationName}</strong><br>
            <small>${latitude.toFixed(6)}, ${longitude.toFixed(6)}</small><br>
            <small class="text-gray-500">50-mile radius center</small>
          </div>
        `))
                .addTo(map.current);

            markers.current.push(centerMarker); // Track the marker

            // Add camping location markers
            campingLocations.forEach((camping, index) => {
                // Create custom camping marker element
                const el = document.createElement('div');
                el.style.cssText = `
          background-color: #22c55e;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.2s ease;
        `;
                el.innerHTML = '🏕️';

                // Add hover effect without transforms (which interfere with MapBox positioning)
                el.onmouseenter = () => {
                    el.style.backgroundColor = '#16a34a';
                    el.style.boxShadow = '0 4px 8px rgba(0,0,0,0.4)';
                    el.style.borderWidth = '4px';
                };
                el.onmouseleave = () => {
                    el.style.backgroundColor = '#22c55e';
                    el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
                    el.style.borderWidth = '3px';
                };

                const campingMarker = new window.mapboxgl.Marker(el)
                    .setLngLat([camping.lng, camping.lat])
                    .setPopup(new window.mapboxgl.Popup({ offset: 25 }).setHTML(`
            <div class="p-3">
              <strong class="text-green-700">🏕️ ${camping.name}</strong><br>
              <span class="text-sm text-gray-600">${camping.type}</span><br>
              <small class="text-gray-500">${camping.lat.toFixed(5)}, ${camping.lng.toFixed(5)}</small><br>
              <div class="mt-2 space-y-1">
                <a href="https://www.google.com/maps/place/${camping.lat},${camping.lng}" 
                   target="_blank" 
                   class="block text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200 text-center">
                  📍 View on Google Maps
                </a>
                <a href="https://www.openstreetmap.org/?mlat=${camping.lat}&mlon=${camping.lng}&zoom=15" 
                   target="_blank" 
                   class="block text-xs bg-green-100 text-green-800 px-2 py-1 rounded hover:bg-green-200 text-center">
                  🗺️ View on OpenStreetMap
                </a>
              </div>
            </div>
          `))
                    .addTo(map.current);

                markers.current.push(campingMarker); // Track the marker
            });

            // Fit map to show all points with padding
            if (campingLocations.length > 0) {
                const bounds = new window.mapboxgl.LngLatBounds();
                bounds.extend([longitude, latitude]);
                campingLocations.forEach(camping => {
                    bounds.extend([camping.lng, camping.lat]);
                });

                map.current.fitBounds(bounds, {
                    padding: 80,
                    maxZoom: 12
                });
            }

            console.log('Map features added successfully');

        } catch (error) {
            console.error('Error adding map features:', error);
            // Don't throw the error, just log it so the app continues working
        }
    }, [latitude, longitude, locationName, campingLocations, mapLoaded, createCircle]);

    const initializeMap = useCallback(() => {
        if (!mapContainer.current) {
            console.error('Container not available during initialization');
            setMapError(true);
            setLoadingMap(false);
            return;
        }

        console.log('Initializing MapBox map...');

        try {
            // Set access token
            window.mapboxgl.accessToken = MAPBOX_TOKEN;

            // Clear container
            mapContainer.current.innerHTML = '';

            // Clean up existing map
            if (map.current) {
                map.current.remove();
                map.current = null;
            }

            // Create map
            map.current = new window.mapboxgl.Map({
                container: mapContainer.current,
                style: getMapStyle(currentStyle),
                center: [longitude, latitude],
                zoom: 8.5
            });

            console.log('Map instance created');

            // Add controls
            map.current.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
            map.current.addControl(new window.mapboxgl.ScaleControl({
                maxWidth: 80,
                unit: 'imperial'
            }), 'bottom-left');

            // Wait for map to load
            map.current.on('load', () => {
                console.log('✅ MapBox map loaded successfully!');
                setMapLoaded(true);
                setLoadingMap(false);
                addMapFeatures();
            });

            map.current.on('error', (e) => {
                console.error('MapBox error:', e);
                setMapError(true);
                setLoadingMap(false);
            });

        } catch (error) {
            console.error('Error initializing MapBox:', error);
            setMapError(true);
            setLoadingMap(false);
        }
    }, [longitude, latitude, currentStyle, MAPBOX_TOKEN, addMapFeatures]);

    const loadMapBox = useCallback(async () => {
        if (!mapContainer.current) {
            console.log('Container not ready, retrying in 200ms...');
            setTimeout(loadMapBox, 200);
            return;
        }

        setLoadingMap(true);
        setMapError(false);
        console.log('Auto-loading MapBox with container:', mapContainer.current);

        try {
            // Load CSS
            if (!document.querySelector('link[href*="mapbox-gl.css"]')) {
                const css = document.createElement('link');
                css.rel = 'stylesheet';
                css.href = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css';
                document.head.appendChild(css);
                console.log('MapBox CSS loaded');
            }

            // Load JS
            if (!window.mapboxgl) {
                console.log('Loading MapBox JS...');
                const script = document.createElement('script');
                script.src = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js';

                script.onload = () => {
                    console.log('MapBox JS loaded successfully');
                    initializeMap();
                };

                script.onerror = (error) => {
                    console.error('Failed to load MapBox JS:', error);
                    setMapError(true);
                    setLoadingMap(false);
                };

                document.head.appendChild(script);
            } else {
                console.log('MapBox already loaded');
                initializeMap();
            }
        } catch (error) {
            console.error('Error loading MapBox:', error);
            setMapError(true);
            setLoadingMap(false);
        }
    }, [initializeMap]);

    // Auto-load MapBox when component mounts
    useEffect(() => {
        const timer = setTimeout(() => {
            loadMapBox();
        }, 100); // Small delay to ensure container is ready

        return () => {
            clearTimeout(timer);
            cleanup(); // Clean up when component unmounts
        };
    }, [loadMapBox, cleanup]); // Added dependencies

    const handleStyleChange = useCallback((style) => {
        setCurrentStyle(style);
        if (map.current && mapLoaded) {
            try {
                map.current.setStyle(getMapStyle(style));
                // Re-add features after style loads
                map.current.once('styledata', () => {
                    setTimeout(() => {
                        addMapFeatures();
                    }, 100);
                });
            } catch (error) {
                console.error('Error changing map style:', error);
            }
        }
    }, [mapLoaded, addMapFeatures]);

    // Update map when coordinates change
    useEffect(() => {
        if (map.current && mapLoaded) {
            try {
                console.log('Updating map center to:', longitude, latitude);
                map.current.setCenter([longitude, latitude]);

                // Small delay before adding features to ensure map has updated
                setTimeout(() => {
                    addMapFeatures();
                }, 100);
            } catch (error) {
                console.error('Error updating map center:', error);
            }
        }
    }, [latitude, longitude, campingLocations, mapLoaded, addMapFeatures]); // Added addMapFeatures dependency

    return (
        <div className="w-full space-y-4">
            {/* Map Style Controls */}
            <div className="flex flex-wrap gap-2 bg-gray-100 p-3 rounded-lg">
                <button
                    onClick={() => handleStyleChange('streets')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        currentStyle === 'streets'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    🛣️ Streets
                </button>
                <button
                    onClick={() => handleStyleChange('terrain')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        currentStyle === 'terrain'
                            ? 'bg-green-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    🏔️ Terrain
                </button>
                <button
                    onClick={() => handleStyleChange('satellite')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        currentStyle === 'satellite'
                            ? 'bg-purple-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    🛰️ Satellite
                </button>
                <button
                    onClick={() => handleStyleChange('dark')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        currentStyle === 'dark'
                            ? 'bg-gray-800 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    🌙 Dark
                </button>
            </div>

            {/* Map Container */}
            <div className="relative">
                <div
                    ref={mapContainer}
                    className="w-full h-96 rounded-lg shadow-lg border border-gray-300 bg-gray-100"
                    style={{ minHeight: '400px' }}
                />

                {/* Map overlay info */}
                {mapLoaded && (
                    <div className="absolute top-2 left-2 bg-white bg-opacity-95 px-3 py-2 rounded-md shadow-md">
                        <div className="text-sm font-medium text-gray-800">📍 {locationName}</div>
                        <div className="text-xs text-gray-600">MapBox Interactive Map</div>
                        <div className="text-xs text-gray-500">{latitude.toFixed(4)}°, {longitude.toFixed(4)}°</div>
                        {campingLocations.length > 0 && (
                            <div className="text-xs text-green-600 mt-1">🏕️ {campingLocations.length} camping locations</div>
                        )}
                        <div className="text-xs text-blue-600 mt-1">
                            Style: {currentStyle.charAt(0).toUpperCase() + currentStyle.slice(1)}
                        </div>
                    </div>
                )}

                {/* Loading state */}
                {loadingMap && (
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center rounded-lg">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                            <div className="text-lg font-semibold text-gray-800 mb-2">Loading MapBox...</div>
                            <div className="text-sm text-gray-600">
                                Initializing interactive map with roads, terrain, and camping locations
                            </div>
                        </div>
                    </div>
                )}

                {/* Error state */}
                {mapError && (
                    <div className="absolute inset-0 bg-red-50 flex items-center justify-center rounded-lg border-2 border-red-200">
                        <div className="text-center p-4">
                            <div className="text-red-500 text-4xl mb-4">⚠️</div>
                            <div className="text-lg font-semibold text-red-800 mb-2">MapBox Error</div>
                            <div className="text-sm text-red-600 mb-4">Failed to load the interactive map</div>
                            <button
                                onClick={loadMapBox}
                                className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 font-medium"
                            >
                                🔄 Retry Loading
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Map Information Panel */}
            <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-3">🗺️ MapBox Interactive Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                        <div><strong>📍 Location:</strong> {locationName}</div>
                        <div><strong>🌐 Coordinates:</strong> {latitude.toFixed(6)}, {longitude.toFixed(6)}</div>
                        <div><strong>📏 Radius:</strong> 50 miles (80.5 km)</div>
                        <div><strong>🏕️ Camping Spots:</strong> {campingLocations.length} locations</div>
                    </div>
                    <div className="space-y-2">
                        <div><strong>🎛️ Interactive Features:</strong></div>
                        <div className="ml-4 text-xs space-y-1">
                            <div>• Auto-loads on page load</div>
                            <div>• Click 🏕️ markers for camping details</div>
                            <div>• Zoom and pan with mouse/touch</div>
                            <div>• Navigation controls (top-right)</div>
                            <div>• Scale indicator (bottom-left)</div>
                            <div>• 4 map styles: Streets, Terrain, Satellite, Dark</div>
                            <div>• 50-mile radius circle with fill</div>
                        </div>
                    </div>
                </div>

                <div className="mt-3 pt-3 border-t border-blue-200">
                    <div className="text-xs text-blue-700">
                        <strong>Current Style:</strong> {
                        currentStyle === 'streets' ? 'Streets - Detailed road networks and urban features' :
                            currentStyle === 'terrain' ? 'Terrain - Topographic data with elevation and outdoor features' :
                                currentStyle === 'satellite' ? 'Satellite - High-resolution aerial imagery with street overlays' :
                                    'Dark - Dark theme optimized for low-light viewing'
                    }
                    </div>
                </div>
            </div>
        </div>
    );
};

// Main app component with GPS input and camping management - keeping the same
const MapBoxGPSApp = () => {
    const [coordinates, setCoordinates] = useState({
        latitude: 36.1437044,
        longitude: -121.564593,
        locationName: "Big Sur"
    });

    const [inputValues, setInputValues] = useState({
        latitude: '36.1437044',
        longitude: '-121.564593',
        locationName: 'Big Sur'
    });

    const [campingLocations, setCampingLocations] = useState([
        // Default camping spots near Big Sur
        { id: 1, name: "Pfeiffer Big Sur State Park", lat: 36.2531, lng: -121.7817, type: "State Park" },
        { id: 2, name: "Julia Pfeiffer Burns State Park", lat: 36.1569, lng: -121.6694, type: "State Park" },
        { id: 3, name: "Andrew Molera State Park", lat: 36.2889, lng: -121.8486, type: "State Park" },
        { id: 4, name: "Limekiln State Park", lat: 36.0033, lng: -121.5119, type: "State Park" }
    ]);

    const [newCamping, setNewCamping] = useState({
        name: '',
        lat: '',
        lng: '',
        type: 'Campground'
    });

    const handleUpdate = () => {
        const lat = parseFloat(inputValues.latitude);
        const lng = parseFloat(inputValues.longitude);

        if (isNaN(lat) || isNaN(lng)) {
            alert('⚠️ Please enter valid numeric coordinates');
            return;
        }

        if (lat < -90 || lat > 90) {
            alert('⚠️ Latitude must be between -90 and 90 degrees');
            return;
        }

        if (lng < -180 || lng > 180) {
            alert('⚠️ Longitude must be between -180 and 180 degrees');
            return;
        }

        setCoordinates({
            latitude: lat,
            longitude: lng,
            locationName: inputValues.locationName.trim() || 'Custom Location'
        });
    };

    const handleAddCamping = () => {
        const lat = parseFloat(newCamping.lat);
        const lng = parseFloat(newCamping.lng);

        if (!newCamping.name.trim()) {
            alert('⚠️ Please enter a camping location name');
            return;
        }

        if (isNaN(lat) || isNaN(lng)) {
            alert('⚠️ Please enter valid coordinates for the camping location');
            return;
        }

        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            alert('⚠️ Coordinates out of range');
            return;
        }

        const newLocation = {
            id: Date.now(),
            name: newCamping.name.trim(),
            lat: lat,
            lng: lng,
            type: newCamping.type
        };

        setCampingLocations(prev => [...prev, newLocation]);
        setNewCamping({ name: '', lat: '', lng: '', type: 'Campground' });
    };

    const handleRemoveCamping = (id) => {
        setCampingLocations(prev => prev.filter(location => location.id !== id));
    };

    const handleCenterOnCamping = (campingLocation) => {
        setCoordinates({
            latitude: campingLocation.lat,
            longitude: campingLocation.lng,
            locationName: campingLocation.name
        });
        setInputValues({
            latitude: campingLocation.lat.toString(),
            longitude: campingLocation.lng.toString(),
            locationName: campingLocation.name
        });
    };

    return (
        <div className="max-w-6xl mx-auto p-4 space-y-6">
            {/* Header */}
            <div className="text-center bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-lg">
                <h1 className="text-3xl font-bold mb-2">🗺️ MapBox GPS Explorer</h1>
                <p className="text-purple-100">Interactive maps • 50-mile radius • Roads • Terrain • Satellite • Camping locations</p>
            </div>

            {/* Input Controls */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">📍 Enter GPS Coordinates</h2>

                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <div className="text-sm font-medium text-gray-700 mb-2">
                                🌐 Latitude (-90 to 90)
                            </div>
                            <input
                                type="number"
                                step="any"
                                value={inputValues.latitude}
                                onChange={(e) => setInputValues(prev => ({ ...prev, latitude: e.target.value }))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="36.1437044"
                            />
                        </div>

                        <div>
                            <div className="text-sm font-medium text-gray-700 mb-2">
                                🌐 Longitude (-180 to 180)
                            </div>
                            <input
                                type="number"
                                step="any"
                                value={inputValues.longitude}
                                onChange={(e) => setInputValues(prev => ({ ...prev, longitude: e.target.value }))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="-121.564593"
                            />
                        </div>

                        <div>
                            <div className="text-sm font-medium text-gray-700 mb-2">
                                📍 Location Name
                            </div>
                            <input
                                type="text"
                                value={inputValues.locationName}
                                onChange={(e) => setInputValues(prev => ({ ...prev, locationName: e.target.value }))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="Big Sur"
                            />
                        </div>
                    </div>

                    <button
                        onClick={handleUpdate}
                        className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 font-medium shadow-md"
                    >
                        🔄 Update Map Location
                    </button>
                </div>
            </div>

            {/* Camping Locations Management */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">🏕️ Camping Locations</h2>

                {/* Add new camping location */}
                <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                    <h3 className="text-lg font-medium mb-3 text-green-800">Add Camping Location</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <input
                            type="text"
                            placeholder="Camping site name"
                            value={newCamping.name}
                            onChange={(e) => setNewCamping(prev => ({ ...prev, name: e.target.value }))}
                            className="px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        <input
                            type="number"
                            step="any"
                            placeholder="Latitude"
                            value={newCamping.lat}
                            onChange={(e) => setNewCamping(prev => ({ ...prev, lat: e.target.value }))}
                            className="px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        <input
                            type="number"
                            step="any"
                            placeholder="Longitude"
                            value={newCamping.lng}
                            onChange={(e) => setNewCamping(prev => ({ ...prev, lng: e.target.value }))}
                            className="px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        <select
                            value={newCamping.type}
                            onChange={(e) => setNewCamping(prev => ({ ...prev, type: e.target.value }))}
                            className="px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                            <option value="Campground">Campground</option>
                            <option value="State Park">State Park</option>
                            <option value="National Park">National Park</option>
                            <option value="RV Park">RV Park</option>
                            <option value="Primitive">Primitive</option>
                            <option value="Backpacking">Backpacking</option>
                        </select>
                    </div>
                    <button
                        onClick={handleAddCamping}
                        className="mt-3 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                    >
                        ➕ Add Camping Location
                    </button>
                </div>

                {/* Existing camping locations list */}
                {campingLocations.length > 0 ? (
                    <div className="space-y-2">
                        <h3 className="text-lg font-medium text-gray-800 mb-3">Current Camping Locations ({campingLocations.length})</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {campingLocations.map(camping => (
                                <div key={camping.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                                    <div className="flex-1">
                                        <div className="font-medium text-gray-800">🏕️ {camping.name}</div>
                                        <div className="text-sm text-gray-600">{camping.type}</div>
                                        <div className="text-xs text-gray-500">{camping.lat.toFixed(5)}, {camping.lng.toFixed(5)}</div>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => handleCenterOnCamping(camping)}
                                            className="text-blue-600 hover:text-blue-800 text-sm px-2 py-1 rounded border border-blue-300 hover:bg-blue-50"
                                            title="Center map on this location"
                                        >
                                            📍 Center
                                        </button>
                                        <button
                                            onClick={() => handleRemoveCamping(camping.id)}
                                            className="text-red-600 hover:text-red-800 text-sm px-2 py-1 rounded border border-red-300 hover:bg-red-50"
                                            title="Remove this camping location"
                                        >
                                            🗑️ Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        <div className="text-4xl mb-2">🏕️</div>
                        <p>No camping locations added yet. Add some camping spots to see them on the map!</p>
                    </div>
                )}
            </div>

            {/* MapBox Component */}
            <MapBoxGPSComponent
                latitude={coordinates.latitude}
                longitude={coordinates.longitude}
                locationName={coordinates.locationName}
                campingLocations={campingLocations}
            />

            {/* Features */}
            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                <h3 className="font-medium text-purple-800 mb-2">✨ MapBox Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <h4 className="font-medium text-purple-700 mb-2">🗺️ Interactive Maps</h4>
                        <ul className="text-sm text-purple-700 space-y-1">
                            <li>• <strong>Multiple Styles:</strong> Streets, Terrain, Satellite, Dark mode</li>
                            <li>• <strong>Smooth Interactions:</strong> Zoom, pan, rotate with mouse/touch</li>
                            <li>• <strong>Vector Maps:</strong> Crisp rendering at any zoom level</li>
                            <li>• <strong>Navigation Controls:</strong> Built-in zoom and compass controls</li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-medium text-purple-700 mb-2">🏕️ Camping Integration</h4>
                        <ul className="text-sm text-purple-700 space-y-1">
                            <li>• <strong>Custom Markers:</strong> 🏕️ icons at exact GPS coordinates</li>
                            <li>• <strong>Interactive Popups:</strong> Click markers for camping details</li>
                            <li>• <strong>Auto-Fit View:</strong> Map adjusts to show all locations</li>
                            <li>• <strong>50-Mile Radius:</strong> Visual circle with semi-transparent fill</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MapBoxGPSApp;